import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormDefinitionService } from '../../core/services/form-definition.service';
import { FormService } from '../../core/services/form.service';
import { FormSumbissionService } from '../../core/services/formSubmission.service';
import { LoggerService } from '../../core/services/logger.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { CheckpointFormHeaderComponent } from '../../shared/components/checkpoint-form-header/checkpoint-form-header.component';
import { CheckboxComponent } from '../../shared/components/form-controls/checkbox/checkbox.component';
import { InputComponent } from '../../shared/components/form-controls/input/input.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../shared/components/modal-dialog/modal-dialog.component';
import { PhotoThumbnailComponent } from '../../shared/components/photo-thumbnail/photo-thumbnail.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../shared/utils/export.utils';
import {
  DownloadDialogComponent,
  DownloadOptions,
} from './components/download-dialog/download-dialog.component';

interface FormSubmissionHistoryRecord {
  id: string;
  checkPoint_id: string;
  form_id: string;
  form_data: any;
  created_at: string;
}

interface FormSubmissionHistoryRecordWithIndex extends FormSubmissionHistoryRecord {
  _searchIndex: string;
}

@Component({
  selector: 'app-form-submission-history',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    ButtonComponent,
    CheckboxComponent,
    CheckpointFormHeaderComponent,
    IconComponent,
    InputComponent,
    SpinnerComponent,
    PhotoThumbnailComponent,
    ModalDialogComponent,
    DownloadDialogComponent,
    TranslatePipe,
    FormsModule,
    HasPermissionDirective,
    TippyDirective,
  ],
  templateUrl: './form-submission-history.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./form-submission-history.component.css'],
})
export class FormSubmissionHistoryComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private interactionService = inject(FormSumbissionService);
  private assetInstanceService = inject(AssetInstanceService);
  private formDefinitionService = inject(FormDefinitionService);
  private translate = inject(TranslationService);
  private toast = inject(ToastService);

  loading = signal(true);
  error = signal<string | null>(null);

  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);
  instance = signal<AssetInstance | null>(null);

  // RQS Pattern - signals from query (assigned in ngOnInit)
  records!: Signal<FormSubmissionHistoryRecord[]>;
  recordsLoading!: Signal<boolean>;
  recordsError!: Signal<string | null>;
  formDefinition!: Signal<FormlyFieldConfig[] | null>;

  private logger = inject(LoggerService).getLogger('form-submission-history-component');

  // Search functionality
  searchTerm = signal('');
  searchTermValue = ''; // For ngModel binding
  private searchTermSubject = new Subject<string>();

  // Records with search index - created once when data loads
  recordsWithIndex = computed<FormSubmissionHistoryRecordWithIndex[]>(() => {
    const rawRecords = this.records();
    this.logger.debug('recordsWithIndex computed - rawRecords length:', rawRecords.length);
    const indexed = rawRecords.map((record: FormSubmissionHistoryRecord) => {
      const searchIndex = this.createSearchIndex(record);
      this.logger.debug('Created search index for record:', record.id, 'index:', searchIndex);
      return {
        ...record,
        _searchIndex: searchIndex,
      };
    });
    this.logger.debug('recordsWithIndex computed - indexed length:', indexed.length);
    return indexed;
  });

  // Filtered records based on search term
  filteredRecords = computed(() => {
    const recordsWithIdx = this.recordsWithIndex();
    const term = this.searchTerm();
    this.logger.debug('filteredRecords computed - recordsWithIdx length:', recordsWithIdx.length);
    this.logger.debug('filteredRecords computed - search term:', term);

    // Return all if no search term or term is too short
    if (!term || term.trim().length < 2) {
      this.logger.debug('filteredRecords - returning all records (no filter)');
      return recordsWithIdx;
    }

    const searchLower = term.trim().toLowerCase();
    const filtered = recordsWithIdx.filter((record) => record._searchIndex.includes(searchLower));
    this.logger.debug('filteredRecords - filtered length:', filtered.length);
    return filtered;
  });

  // Form definition fields extracted from form definition signal
  formDefinitionFields = computed(() => {
    const formDef = this.formDefinition();
    if (!formDef) return [];

    const fieldKeys: string[] = [];
    formDef.forEach((field: FormlyFieldConfig) => {
      if (field.key && typeof field.key === 'string') {
        fieldKeys.push(field.key);
      }
    });
    return fieldKeys;
  });

  // Toggle to show legacy fields not in current form definition
  showLegacyFields = signal(false);
  showLegacyFieldsValue = false; // For ngModel binding

  // Delete confirmation modal
  showDeleteModal = signal(false);
  recordToDelete = signal<string | null>(null);

  // Download dialog state
  showDownloadDialog = signal(false);

  // All unique fields found in historical records
  allHistoricalFields = computed(() => {
    const currentRecords = this.records();
    if (!currentRecords || currentRecords.length === 0) {
      return [];
    }

    const allKeys = new Set<string>();
    const systemFields = ['created_at', 'updated_at', 'id'];
    currentRecords.forEach((record: FormSubmissionHistoryRecord) => {
      if (record.form_data) {
        Object.keys(record.form_data).forEach((key) => {
          // Filter out system fields that would conflict with database columns
          if (!systemFields.includes(key)) {
            allKeys.add(key);
          }
        });
      }
    });

    return Array.from(allKeys);
  });

  // Legacy fields (in records but not in current form definition)
  legacyFields = computed(() => {
    const historical = this.allHistoricalFields();
    const current = this.formDefinitionFields();
    // Filter out _recorded_by from legacy fields since we always show it
    return historical.filter((field) => !current.includes(field) && field !== '_recorded_by');
  });

  // Column headers to display - based on form definition + optional legacy fields
  columns = computed(() => {
    const current = this.formDefinitionFields();
    this.logger.debug('columns computed - formDefinitionFields:', current);
    this.logger.debug('columns computed - showLegacyFields:', this.showLegacyFields());

    // Filter out text-label fields (read-only, never have submission data)
    const currentWithoutTextLabels = current.filter((column) => !this.isTextLabelField(column));

    // Always show _recorded_by as the first column
    const columnsWithRecordedBy = ['_recorded_by', ...currentWithoutTextLabels];

    if (this.showLegacyFields()) {
      const legacy = this.legacyFields();
      this.logger.debug('columns computed - legacyFields:', legacy);
      return [...columnsWithRecordedBy, ...legacy];
    }
    return columnsWithRecordedBy;
  });

  async ngOnInit() {
    this.logger.debug('ngOnInit - starting');
    try {
      const checkPointId = this.route.snapshot.paramMap.get('checkpointId');
      const formId = this.route.snapshot.paramMap.get('formId');
      const instanceId = this.route.snapshot.paramMap.get('instanceId');
      this.logger.debug(
        'ngOnInit - checkpointId:',
        checkPointId,
        'formId:',
        formId,
        'instanceId:',
        instanceId,
      );

      if (!checkPointId || !formId) {
        this.logger.error('ngOnInit - Missing asset or interaction type ID');
        this.error.set('Missing asset or interaction type ID');
        this.loading.set(false);
        return;
      }

      // Load asset and interaction type details
      const [checkPoint, form] = await Promise.all([
        this.checkPointService.getById(checkPointId),
        this.formService.getById(formId),
      ]);
      this.logger.debug('ngOnInit - loaded asset:', checkPoint);
      this.logger.debug('ngOnInit - loaded interactionType:', form);

      if (!checkPoint || !form) {
        this.logger.error('ngOnInit - Asset or interaction type not found');
        this.error.set('Asset or interaction type not found');
        this.loading.set(false);
        return;
      }

      this.checkPoint.set(checkPoint);
      this.form.set(form);

      // Load instance if instanceId is present
      if (instanceId) {
        const instance = await this.assetInstanceService.getById(instanceId);
        this.instance.set(instance);
        this.breadcrumbService.setDynamicLabels([
          checkPoint.name,
          form.name,
          `#${instance?.serial_number ?? instanceId}`,
        ]);
      } else {
        this.breadcrumbService.setDynamicLabels([checkPoint.name, form.name]);
      }
      this.logger.debug('ngOnInit - set asset and interactionType signals');

      // Create reactive query for form definition that auto-updates on sync changes
      if (form.form_definition_id) {
        this.logger.debug('ngOnInit - creating form definition query:', form.form_definition_id);
        const formDefQuery = this.formDefinitionService.createQueryById(form.form_definition_id);
        this.formDefinition = formDefQuery.data;
        this.logger.debug('ngOnInit - formDefinition signal assigned');
      } else {
        // If no form definition ID, create a signal that returns null
        this.formDefinition = signal(null);
        this.logger.debug('ngOnInit - no form definition ID, using null signal');
      }

      // Create reactive query that auto-updates on sync changes
      this.logger.debug('ngOnInit - creating records query');
      let recordsQuery;
      if (instanceId) {
        recordsQuery = this.interactionService.createQueryByCheckPointAndTypeAndInstance(
          checkPointId,
          formId,
          instanceId,
        );
      } else {
        recordsQuery = this.interactionService.createQueryByCheckPointAndType(checkPointId, formId);
      }
      // Assign the signals directly from the query (RQS pattern)
      this.records = recordsQuery.data;
      this.recordsLoading = recordsQuery.loading;
      this.recordsError = recordsQuery.error;
      this.logger.debug('ngOnInit - record signals assigned');

      // Set up debounced search
      this.searchTermSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((term) => {
        this.logger.debug('search term changed:', term);
        this.searchTerm.set(term);
      });

      this.logger.debug('ngOnInit - setting loading to false');
      this.loading.set(false);
      this.logger.debug('ngOnInit - complete');
    } catch (err) {
      this.logger.error('ngOnInit - error:', err);
      this.logger.error('Failed to load interaction history:', err);
      this.error.set('Failed to load interaction history');
      this.loading.set(false);
    }
  }

  formatDate(dateString: string): { date: string; time: string } {
    // Patch: treat DB timestamps as UTC
    if (!dateString) return { date: '', time: '' };
    // If already ISO, use as is; else convert
    const iso = dateString.includes('T') ? dateString : dateString.replace(' ', 'T') + 'Z';
    const dateObj = new Date(iso);

    // Format date and time separately with same locale settings
    const date = dateObj.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const time = dateObj.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });

    return { date, time };
  }

  getCellValue(record: FormSubmissionHistoryRecord, column: string): any {
    const rawValue = record.form_data?.[column] ?? '';

    const formDef = this.formDefinition();
    if (!formDef) return rawValue;

    const field = formDef.find((f: FormlyFieldConfig) => f.key === column);
    if (!field) return rawValue;

    const fieldType = field.type as string;
    const options = field.props?.['options'] as { label: string; value: string }[] | undefined;

    switch (fieldType) {
      case 'radio':
      case 'dropdown': {
        // Single value — look up the option label
        if (!options || !rawValue) return rawValue;
        return options.find((o) => o.value === rawValue)?.label ?? rawValue;
      }

      case 'checkbox-group':
      case 'select': {
        // May be single or array of values — map each to its option label
        if (!options || !rawValue) return rawValue;
        if (Array.isArray(rawValue)) {
          return rawValue.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ');
        }
        return options.find((o) => o.value === rawValue)?.label ?? rawValue;
      }

      default:
        return rawValue;
    }
  }

  getColumnLabel(column: string): string {
    if (column === '_recorded_by') {
      return this.translate.translate('submission.history.recordedBy');
    }
    const formDef = this.formDefinition();
    if (formDef) {
      const field = formDef.find((f: FormlyFieldConfig) => f.key === column);
      if (field?.props?.label) {
        return field.props.label as string;
      }
    }
    return column;
  }

  isPhotoField(column: string): boolean {
    const formDef = this.formDefinition();
    if (!formDef) return false;
    const field = formDef.find((f: FormlyFieldConfig) => f.key === column);
    return field?.type === 'photo';
  }

  isTextLabelField(column: string): boolean {
    const formDef = this.formDefinition();
    if (!formDef) return false;
    const field = formDef.find((f: FormlyFieldConfig) => f.key === column);
    return field?.type === 'text-label';
  }

  getPhotoUrls(record: FormSubmissionHistoryRecord, column: string): string[] {
    const value = this.getCellValue(record, column);
    if (!value) return [];
    // Handle both single photo (string) and multiple photos (array)
    return Array.isArray(value) ? value : [value];
  }

  showDeleteConfirmation(id: string, event: Event) {
    event.stopPropagation();
    this.recordToDelete.set(id);
    this.showDeleteModal.set(true);
  }

  viewRecord(id: string, event: Event): void {
    event.stopPropagation();
    const checkpointId = this.route.snapshot.paramMap.get('checkpointId');
    const formId = this.route.snapshot.paramMap.get('formId');
    const instanceId = this.route.snapshot.paramMap.get('instanceId');

    const segments = instanceId
      ? ['/submission', checkpointId, formId, instanceId, 'history', id]
      : ['/submission', checkpointId, formId, 'history', id];
    this.router.navigate(segments);
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
    this.recordToDelete.set(null);
  }

  async confirmDelete() {
    const id = this.recordToDelete();
    if (!id) return;

    try {
      this.showDeleteModal.set(false);
      await this.interactionService.delete(id);
      // No need to manually reload - query will auto-refresh via notifyDataChange
    } catch (err) {
      this.logger.error('Failed to delete interaction record:', err);
      this.error.set('Failed to delete interaction record. Please try again.');
    } finally {
      this.recordToDelete.set(null);
    }
  }

  /**
   * Create a search index string for a record
   * Concatenates all visible field values with a safe delimiter
   * Uses the same date/time formatting as displayed in the table
   */
  private createSearchIndex(record: FormSubmissionHistoryRecord): string {
    const DELIMITER = '|||'; // Safe delimiter unlikely to appear in data
    const parts: string[] = [];

    // Add formatted date and time
    const { date, time } = this.formatDate(record.created_at);
    parts.push(date, time);

    // Add day of week (invisible indexed term)
    const iso = record.created_at.includes('T')
      ? record.created_at
      : record.created_at.replace(' ', 'T') + 'Z';
    const dateObj = new Date(iso);
    const dayOfWeek = dateObj.toLocaleDateString(undefined, { weekday: 'long' });
    parts.push(dayOfWeek);

    // Add all column values (excluding photo and text-label fields)
    const cols = this.columns();
    cols.forEach((column) => {
      // Skip photo fields (blob data) and text-label fields (no user data)
      if (this.isPhotoField(column) || this.isTextLabelField(column)) {
        return;
      }
      const value = this.getCellValue(record, column);
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          parts.push(value.join(' '));
        } else {
          parts.push(String(value));
        }
      }
    });

    // Join with delimiter and convert to lowercase for case-insensitive search
    return parts.join(DELIMITER).toLowerCase();
  }

  /**
   * Handle search input changes with debouncing
   */
  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTermSubject.next(value);
  }

  /**
   * Clear search input and reset filter
   */
  clearSearch(): void {
    this.searchTermValue = '';
    this.searchTerm.set('');
    this.searchTermSubject.next('');
  }

  /**
   * Navigate to fill out the form for this checkpoint and interaction type
   */
  fillOutForm(): void {
    const checkPointId = this.route.snapshot.paramMap.get('checkpointId');
    const formId = this.route.snapshot.paramMap.get('formId');
    const instanceId = this.route.snapshot.paramMap.get('instanceId');
    if (checkPointId && formId) {
      const segments = ['/submission', checkPointId, formId];
      if (instanceId) segments.push(instanceId);
      const returnUrl = instanceId
        ? `/submission/${checkPointId}/${formId}/${instanceId}/history`
        : `/submission/${checkPointId}/${formId}/history`;
      this.router.navigate(segments, { queryParams: { returnUrl } });
    }
  }

  /**
   * Navigate to edit the form associated with this interaction type
   */
  editForm(): void {
    const formId = this.route.snapshot.paramMap.get('formId');
    if (formId) {
      this.router.navigate(['/forms', formId]);
    }
  }

  /**
   * Open the download dialog
   */
  openDownloadDialog(): void {
    this.showDownloadDialog.set(true);
  }

  /**
   * Close the download dialog
   */
  closeDownloadDialog(): void {
    this.showDownloadDialog.set(false);
  }

  /**
   * Handle download action from dialog
   */
  async handleDownload(options: DownloadOptions): Promise<void> {
    this.closeDownloadDialog();

    try {
      // Determine which records to export
      const recordsToExport = options.scope === 'all' ? this.records() : this.filteredRecords();

      if (recordsToExport.length === 0) {
        this.toast.warning(this.translate.translate('submission.download.noData'));
        return;
      }

      // Prepare data for export
      const exportData = this.prepareExportData(recordsToExport);

      // Generate filename
      const checkPointName = this.checkPoint()?.name || 'unknown';
      const filename = generateFilename('submissions', checkPointName);

      // Export based on format
      if (options.format === 'csv') {
        exportToCSV(exportData, filename);
      } else {
        exportToXLSX(exportData, filename);
      }

      this.toast.success(
        this.translate.translate('submission.download.success', {
          count: recordsToExport.length.toString(),
        }),
      );
    } catch (error) {
      this.logger.error('Failed to export data:', error);
      this.toast.error(this.translate.translate('submission.download.error'));
    }
  }

  /**
   * Prepare interaction records for export
   * Excludes photo fields and internal metadata
   */
  private prepareExportData(records: FormSubmissionHistoryRecord[]): any[] {
    const formDef = this.formDefinition();

    return records.map((record) => {
      const exportRow: any = {};

      // Add submission ID
      exportRow['Submission ID'] = record.id;

      // Add timestamp
      const { date, time } = this.formatDate(record.created_at);
      exportRow['Date'] = date;
      exportRow['Time'] = time;

      // Add form fields (excluding photos and text-labels)
      const cols = this.columns();
      cols.forEach((column) => {
        // Skip photo fields and text-label fields (read-only, never have data)
        if (this.isPhotoField(column) || this.isTextLabelField(column)) {
          return;
        }

        const value = this.getCellValue(record, column);
        const label = this.getColumnLabel(column);

        // Handle different value types
        if (value === null || value === undefined || value === '') {
          exportRow[label] = '';
        } else if (Array.isArray(value)) {
          exportRow[label] = value.join(', ');
        } else {
          exportRow[label] = String(value);
        }
      });

      return exportRow;
    });
  }
}
